import dayjs from "dayjs";
import { Formik } from "formik";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft } from "react-icons/bi";
import { useLocation } from "react-router-dom";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { DATE_FORMAT } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import structureValidation from "./structureValidation";

const IsoleDuoFamilleView = ({ onSubmit, data, cancel, locked = false }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const { state } = useLocation();
  const CREATE = t("CREATE", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const STRUCTURE = t("STRUCTURE_SELECT", { returnObjects: true });
  const [insurancePlans, setInsurancePlans] = useState([]);

  useQuery(INSURANCE_PLANS, {
    variables: { take: null },
    onCompleted: ({ insurancePlans: { data: plans } }) => {
      const formattedPlans = plans.map(({ id, name, internal, risk }) => ({
        value: id,
        label: name,
        internal,
        risk,
      }));
      const risk = get(state, "risk");
      setInsurancePlans(
        risk
          ? formattedPlans.filter((plan) => plan.risk === risk)
          : formattedPlans,
      );
    },
  });

  return (
    <Formik
      initialValues={{
        name: get(data, "name", "") || "",
        insurancePlan: {
          id: get(data, "insurancePlan.id", ""),
          name: get(data, "insurancePlan.name", ""),
        },
        amounts: {
          single: get(data, "prices.amounts.single", ""),
          duo: get(data, "prices.amounts.duo", ""),
          family: get(data, "prices.amounts.family", ""),
        },
        dates:
          get(data, "startDate") && get(data, "endDate")
            ? [dayjs(data.startDate), dayjs(data.endDate)]
            : [],
      }}
      validationSchema={structureValidation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, setFieldValue, values, ...formProps }) => (
        <>
          <Header
            title={CREATE.HEADER.TITLE}
            subtitle={CREATE.HEADER.ACTION}
            actions={[
              {
                text: CREATE.HEADER.DISCARD,
                onClick: cancel,
                type: "primary--outlined",
                icon: <BiChevronLeft size={20} />,
              },
              {
                text: CREATE.HEADER.SAVE,
                onClick: handleSubmit,
                type: "primary",
                icon: <BiCheck size={20} />,
              },
            ]}
          />
          <Card title={FORM.GENERAL_INFO}>
            <div className="structure-locked-banner mb-16 p-16">
              <span className="structure-locked-banner__label">
                {STRUCTURE.STRUCTURE_TITLE}
              </span>
            </div>
            <div className="grid--2">
              <Select
                options={insurancePlans}
                placeholder={FORM.INSURANCE_PLAN}
                label={FORM.INSURANCE_PLAN}
                value={get(values, "insurancePlan.id")}
                {...bindInputProps({
                  name: "insurancePlan.id",
                  values,
                  ...formProps,
                })}
                onChange={(id) => setFieldValue("insurancePlan.id", id || "")}
              />
              <Input
                label={FORM.NAME}
                placeholder={FORM.NAME}
                {...bindInputProps({
                  name: "name",
                  values,
                  ...formProps,
                })}
              />
              <Input
                type="date"
                variant="range"
                label={FORM.DATE}
                format={DATE_FORMAT}
                placeholders={[FORM.START_DATE, FORM.END_DATE]}
                {...bindInputProps({
                  name: "dates",
                  values,
                  ...formProps,
                })}
                defaultValue={values.dates}
                onChange={(value) => setFieldValue("dates", value)}
              />
            </div>
          </Card>
          <div key="div" className="m-top-24" />
          <Card title={STRUCTURE.AMOUNTS_TITLE}>
            <div className="grid--3">
              <Input
                type="number"
                label={STRUCTURE.TYPOLOGIES.SINGLE}
                placeholder={FORM.FIXED_PRICE}
                {...bindInputProps({
                  name: "amounts.single",
                  values,
                  ...formProps,
                })}
              />
              <Input
                type="number"
                label={STRUCTURE.TYPOLOGIES.DUO}
                placeholder={FORM.FIXED_PRICE}
                {...bindInputProps({
                  name: "amounts.duo",
                  values,
                  ...formProps,
                })}
              />
              <Input
                type="number"
                label={STRUCTURE.TYPOLOGIES.FAMILY}
                placeholder={FORM.FIXED_PRICE}
                {...bindInputProps({
                  name: "amounts.family",
                  values,
                  ...formProps,
                })}
              />
            </div>
            {locked && (
              <p className="structure-locked-banner__hint mt-12">
                {STRUCTURE.LOCKED_HINT}
              </p>
            )}
          </Card>
        </>
      )}
    </Formik>
  );
};

export default IsoleDuoFamilleView;
